/**
 * PRS Database Readiness Dashboard (E-015 / US-042)
 *
 * Traffic-light scorecard per property + portfolio summary, with a
 * drill-down list of missing fields when a RED row is expanded.
 *
 * Acceptance criteria (US-042):
 *  ✓ Traffic light status (Green / Amber / Red)
 *  ✓ Clicking a red property shows exactly which fields are missing
 *  ✓ Portfolio-level readiness percentage
 *  ✓ Refresh on demand (auto-fetches when mounted)
 *
 * NOTE: the underlying field spec is a PLACEHOLDER until T-039-001
 * publishes the real MHCLG field list. Swap src/config/prsSpec.js
 * server-side and this UI reflects it automatically.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { prsService, PrsReadinessReport, PrsReadinessItem, PrsImportJobStatus } from '../../../services/api';
import { getAppMarket } from '../../../services/MarketService';
import PRSWizard from './PRSWizard';
import { safeText } from '../../../utils/formatAddress';

const STATUS_STYLES: Record<PrsReadinessItem['status'], { bg: string; text: string; dot: string; label: string }> = {
    GREEN: { bg: 'bg-emerald-50',  text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Ready' },
    AMBER: { bg: 'bg-amber-50',    text: 'text-amber-700',   dot: 'bg-amber-500',   label: 'Partial' },
    RED:   { bg: 'bg-rose-50',     text: 'text-rose-700',    dot: 'bg-rose-500',    label: 'Action Required' },
};

const PRSReadiness: React.FC = () => {
    // F-307: Texas market brands this readiness card as Texas Registration.
    const isTexas = getAppMarket() === 'texas';
    const [report, setReport] = useState<PrsReadinessReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState<string | null>(null);
    // 403 means "feature requires a paid plan" — we hide the widget entirely
    // for trial/freemium accounts rather than surfacing an upgrade nudge
    // here. The existing UpgradeModal flow handles plan upsell elsewhere.
    const [planGated, setPlanGated] = useState(false);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    // Click a headline stat card to filter the row list below it. e.g.
    // "Action Required = 4" → click → list shows only the 4 RED rows.
    // The Portfolio (total) tile acts as "clear filter".
    const [statusFilter, setStatusFilter] = useState<'ALL' | 'GREEN' | 'AMBER' | 'RED'>('ALL');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await prsService.getReadiness();
            setReport(res.data?.data || null);
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 403 || status === 402) {
                setPlanGated(true);
                return;
            }
            setError(e?.response?.data?.message || e?.message || (getAppMarket() === 'texas' ? 'Failed to load registration readiness' : 'Failed to load PRS readiness'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleRow = (propertyId: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(propertyId)) next.delete(propertyId);
            else next.add(propertyId);
            return next;
        });
    };

    const handlePortfolioExport = async () => {
        setExporting(true);
        try {
            const res = await prsService.exportCsv();
            const blob = new Blob([res.data as any], { type: 'text/csv;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `prs-export-portfolio-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e: any) {
            alert(e?.response?.data?.message || e?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const importPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const showImportResult = (job: PrsImportJobStatus) => {
        const r = job.result;
        if (!r) {
            alert('Import completed but no result data was returned.');
            return;
        }
        const lines = [
            `Imported successfully.`,
            ``,
            `Total rows:  ${r.total ?? 0}`,
            `Updated:     ${r.updated ?? 0}`,
            `Skipped:     ${r.skipped ?? 0} (matched but no editable column changed)`,
            `Not matched: ${r.notMatched ?? 0}`,
        ];
        if (r.ignoredEdits && r.ignoredEdits > 0) {
            lines.push('');
            lines.push(`⚠ ${r.ignoredEdits} cell${r.ignoredEdits === 1 ? '' : 's'} could not be applied:`);
            if (r.ignoredFieldHints?.length) {
                for (const h of r.ignoredFieldHints) {
                    lines.push(`  • ${h.key} (${h.count}×) — ${h.ownerUi}`);
                }
            }
        }
        alert(lines.join('\n'));
        load();
    };

    const handleImportPicked = async (file: File) => {
        setImporting(true);
        try {
            const csvText = await file.text();
            const res = await prsService.importCsv(csvText);
            const jobId = res.data?.jobId;
            if (!jobId) throw new Error('No jobId returned from server');

            importPollRef.current = setInterval(async () => {
                try {
                    const statusRes = await prsService.getImportStatus(jobId);
                    const job = statusRes.data?.data;
                    if (!job) return;
                    if (job.status === 'COMPLETED') {
                        if (importPollRef.current) clearInterval(importPollRef.current);
                        importPollRef.current = null;
                        setImporting(false);
                        showImportResult(job);
                    } else if (job.status === 'FAILED') {
                        if (importPollRef.current) clearInterval(importPollRef.current);
                        importPollRef.current = null;
                        setImporting(false);
                        alert(job.error || 'Import failed');
                        load();
                    }
                } catch {
                    // poll again
                }
            }, 3000);
        } catch (e: any) {
            setImporting(false);
            alert(e?.response?.data?.message || e?.message || 'Import failed');
        }
    };

    // Cleanup poll on unmount
    useEffect(() => {
        return () => {
            if (importPollRef.current) clearInterval(importPollRef.current);
        };
    }, []);

    // Trial / freemium accounts: silent hide. The widget simply doesn't
    // render — same effect as the feature not existing for them.
    if (planGated) return null;

    if (loading) {
        return (
            <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
                <i className="fas fa-spinner fa-spin mr-2"></i>{isTexas ? 'Loading registration readiness…' : 'Loading PRS readiness…'}
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="text-rose-700 text-sm font-medium"><i className="fas fa-exclamation-circle mr-2"></i>{error}</div>
                <button onClick={load} className="mt-3 text-xs font-bold text-emerald-700 hover:text-emerald-900 uppercase tracking-wider">Retry</button>
            </div>
        );
    }

    if (!report) return null;
    const { items, summary } = report;
    const visibleItems = statusFilter === 'ALL' ? items : items.filter(i => i.status === statusFilter);
    const filterLabel: Record<typeof statusFilter, string> = {
        ALL: 'All properties',
        GREEN: 'Ready (Green)',
        AMBER: 'Partial (Amber)',
        RED: 'Action Required (Red)',
    };

    return (
        <>
        <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                        <i className={`fas ${isTexas ? 'fa-clipboard-check text-emerald-600' : 'fa-database text-indigo-600'} mr-2`}></i>
                        {isTexas ? 'Texas Registration Readiness' : 'PRS Database Readiness'}
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                        {isTexas
                            ? 'Per-property status against Texas rental registration requirements.'
                            : 'Per-property status against the MHCLG PRS Database field specification.'}
                        Export a CSV when you're ready to upload.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowWizard(true)}
                        className="inline-flex items-center px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700"
                    >
                        <i className="fas fa-wand-magic mr-1.5"></i>
                        Launch Wizard
                    </button>
                    <button
                        onClick={load}
                        className="text-xs font-bold text-gray-500 hover:text-gray-900 uppercase tracking-wider"
                        title="Refresh"
                    >
                        <i className="fas fa-sync-alt mr-1"></i> Refresh
                    </button>
                    <label className={`inline-flex items-center px-3 py-2 bg-white border border-indigo-300 text-indigo-700 text-xs font-bold rounded-lg hover:bg-indigo-50 cursor-pointer ${importing ? 'opacity-50 cursor-wait' : ''}`}>
                        <i className={`fas ${importing ? 'fa-spinner fa-spin' : 'fa-file-import'} mr-2`}></i>
                        {importing ? 'Processing import…' : 'Import edited CSV'}
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            disabled={importing}
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleImportPicked(f);
                                e.target.value = '';
                            }}
                        />
                    </label>
                    <button
                        onClick={handlePortfolioExport}
                        disabled={exporting || items.length === 0}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                        <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-file-export'} mr-2`}></i>
                        {exporting ? 'Generating…' : (isTexas ? 'Export Registration CSV' : 'Export PRS CSV')}
                    </button>
                </div>
            </div>

            {/* Summary stats — click any of the first four to filter the list
                below to just those properties. The Readiness % tile is a
                derived metric, not a category, so it stays non-interactive. */}
            <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-5 gap-4 border-b border-gray-100 bg-gray-50">
                <Stat
                    label="Portfolio"
                    value={summary.total}
                    hint="properties"
                    onClick={() => setStatusFilter('ALL')}
                    active={statusFilter === 'ALL'}
                    activeRing="ring-slate-400"
                />
                <Stat
                    label="Ready"
                    value={summary.green}
                    dotColor="bg-emerald-500"
                    onClick={() => setStatusFilter('GREEN')}
                    active={statusFilter === 'GREEN'}
                    activeRing="ring-emerald-400"
                />
                <Stat
                    label="Partial"
                    value={summary.amber}
                    dotColor="bg-amber-500"
                    onClick={() => setStatusFilter('AMBER')}
                    active={statusFilter === 'AMBER'}
                    activeRing="ring-amber-400"
                />
                <Stat
                    label="Action Required"
                    value={summary.red}
                    dotColor="bg-rose-500"
                    onClick={() => setStatusFilter('RED')}
                    active={statusFilter === 'RED'}
                    activeRing="ring-rose-400"
                />
                <Stat label="Readiness" value={`${summary.readinessPercent}%`} hint={`avg score ${summary.averageScore}%`} />
            </div>

            {/* Active filter banner */}
            {statusFilter !== 'ALL' && (
                <div className="px-6 py-2 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between text-xs">
                    <span className="text-indigo-700">
                        <i className="fas fa-filter mr-2"></i>
                        Showing <strong>{filterLabel[statusFilter]}</strong>
                        {' '}— {visibleItems.length} of {items.length}
                    </span>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('ALL')}
                        className="font-bold text-indigo-700 hover:text-indigo-900 uppercase tracking-wider"
                    >
                        Clear filter
                    </button>
                </div>
            )}

            {/* Per-property rows */}
            <ul className="divide-y divide-gray-100">
                {items.length === 0 && (
                    <li className="px-6 py-8 text-center text-sm text-gray-500">
                        No properties yet. Add a property to see {isTexas ? 'registration' : 'PRS'} readiness scoring.
                    </li>
                )}
                {items.length > 0 && visibleItems.length === 0 && (
                    <li className="px-6 py-8 text-center text-sm text-gray-500">
                        No {filterLabel[statusFilter].toLowerCase()} properties in the portfolio.
                    </li>
                )}
                {visibleItems.map(item => {
                    const s = STATUS_STYLES[item.status];
                    const isOpen = expanded.has(item.propertyId);
                    return (
                        <li key={item.propertyId} className="px-6 py-3">
                            <button
                                onClick={() => toggleRow(item.propertyId)}
                                className="w-full flex items-center justify-between text-left hover:bg-gray-50 -mx-2 px-2 py-1 rounded"
                            >
                                <div className="flex items-center min-w-0 gap-3">
                                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.dot}`} aria-hidden="true"></span>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">{safeText(item.addressLine1, `Property ${item.propertyId}`)}</div>
                                        <div className="text-[11px] text-gray-500">
                                            Score: <span className="font-bold">{item.score}%</span>
                                            {item.missingRequired.length > 0 && (
                                                <span className="ml-2 text-rose-600">
                                                    · {item.missingRequired.length} required field{item.missingRequired.length === 1 ? '' : 's'} missing
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.text}`}>
                                        {s.label}
                                    </span>
                                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-xs text-gray-400`}></i>
                                </div>
                            </button>

                            {isOpen && (
                                <div className="mt-2 ml-6 p-3 bg-gray-50 rounded text-xs">
                                    {item.missingRequired.length === 0 && item.missingOptional.length === 0 && (
                                        <p className="text-emerald-700 font-medium">All {isTexas ? 'registration' : 'PRS'} fields populated. Ready for export.</p>
                                    )}
                                    {item.missingRequired.length > 0 && (
                                        <div className="mb-2">
                                            <div className="font-bold text-rose-700 mb-1">
                                                <i className="fas fa-exclamation-circle mr-1"></i>
                                                Required fields missing ({item.missingRequired.length})
                                            </div>
                                            <ul className="list-disc list-inside text-rose-700 space-y-0.5">
                                                {item.missingRequired.map(f => (
                                                    <li key={f.key}>{safeText(f.label, f.key)}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {item.missingOptional.length > 0 && (
                                        <div>
                                            <div className="font-bold text-amber-700 mb-1">
                                                <i className="fas fa-info-circle mr-1"></i>
                                                Optional fields missing ({item.missingOptional.length})
                                            </div>
                                            <ul className="list-disc list-inside text-amber-700 space-y-0.5">
                                                {item.missingOptional.map(f => (
                                                    <li key={f.key}>{safeText(f.label, f.key)}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
        <PRSWizard isOpen={showWizard} onClose={() => setShowWizard(false)} onSuccess={load} />
        </>
    );
};

const Stat: React.FC<{
    label: string;
    value: React.ReactNode;
    hint?: string;
    dotColor?: string;
    onClick?: () => void;
    active?: boolean;
    activeRing?: string;   // Tailwind ring colour class for the active state
}> = ({ label, value, hint, dotColor, onClick, active, activeRing }) => {
    const clickable = typeof onClick === 'function';
    const body = (
        <>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                {dotColor && <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} aria-hidden="true"></span>}
                {label}
            </div>
            <div className="mt-1 text-2xl font-black text-gray-900">{value}</div>
            {hint && <div className="text-[10px] text-gray-400">{hint}</div>}
        </>
    );
    if (!clickable) {
        return <div>{body}</div>;
    }
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={!!active}
            className={`text-left bg-white rounded-lg px-3 py-2 border transition-all hover:shadow-sm ${
                active
                    ? `border-transparent ring-2 ${activeRing || 'ring-indigo-400'} shadow-sm`
                    : 'border-gray-100 hover:border-gray-200'
            }`}
        >
            {body}
        </button>
    );
};

export default PRSReadiness;
